import React from 'react';
import {
  Image,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { Text } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAppColors } from '../../hooks/useAppColors';
import { useThemeStore } from '../../stores/themeStore';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/common/LanguageSwitcher';

interface AuthLayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  useKeyboardAvoiding?: boolean;
}

export default function AuthLayout({
  children,
  title,
  subtitle,
  useKeyboardAvoiding = false,
}: AuthLayoutProps) {
  const { t } = useTranslation();
  const colors = useAppColors();
  const theme = useThemeStore((s) => s.mode);
  const insets = useSafeAreaInsets();

  const content = (
    <>
      <View style={[styles.languageSwitcherAbsolute, { top: 4 + insets.top }]}>
        <LanguageSwitcher />
      </View>
      <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
        <View style={styles.header}>
          <Image
            source={
              theme === 'dark'
                ? require('../../../assets/images/cbjj-logo-white.png')
                : require('../../../assets/images/cbjj-logo-black.png')
            }
            style={styles.logo}
            resizeMode="contain"
          />
          {title && <Text style={[styles.title, { color: colors.text }]}>{title}</Text>}
          {subtitle && <Text style={[styles.subtitle, { color: colors.textSecondary }]}>{subtitle}</Text>}
        </View>
        <View style={styles.form}>{children}</View>
      </ScrollView>
    </>
  );

  if (useKeyboardAvoiding) {
    return (
      <KeyboardAvoidingView
        style={[styles.flex, { backgroundColor: colors.bg }]}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        {content}
      </KeyboardAvoidingView>
    );
  }

  return <View style={[styles.flex, { backgroundColor: colors.bg }]}>{content}</View>;
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  languageSwitcherAbsolute: {
    position: 'absolute',
    top: 16,
    right: 16,
    zIndex: 10,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    padding: 24,
  },
  header: {
    alignItems: 'center',
    marginBottom: 32,
    gap: 4,
  },
  logo: {
    width: 200,
    height: 80,
    marginBottom: 16,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
  },
  subtitle: {
    fontSize: 15,
    textAlign: 'center',
    paddingHorizontal: 16,
  },
  form: {
    width: '100%',
    maxWidth: 400,
    alignSelf: 'center',
  },
});
